import { Component, EventEmitter, Input, Output, OnDestroy } from '@angular/core';
import { SparePartModel } from 'src/app/models/spare-part-model/spare-part-model';
import { environment } from 'src/environments/environment';
import { SparePartsService } from 'src/app/services/spareParts/spare-parts.service';
import { Subscription, interval } from 'rxjs';
import { take } from 'rxjs/operators';

@Component({
  selector: 'app-status-changer',
  templateUrl: './status-changer.component.html',
  styleUrls: ['./status-changer.component.css']
})
export class StatusChangerComponent implements OnDestroy {

  @Input() selectedItems: SparePartModel[] = [];
  @Input() isSelectingAllFiltered: boolean = false;
  @Input() allFilteredCount: number = 0;
  @Input() searchTerm: string = '';
  @Input() startDate?: Date;
  @Input() endDate?: Date;
  
  @Output() close = new EventEmitter<void>(); // Evento para cerrar el componente
  @Output() statusChange = new EventEmitter<{status: string, success: boolean}>(); // Evento para aplicar el cambio de estado

  selectedStatus: string = ''; // Estado seleccionado en el dropdown
  sparePartDeliveryStatus = environment.sparePartDeliveryStatus; // Lista de estados
  isProcessing: boolean = false; // Indica si se está procesando la actualización
  
  // Variables para mostrar el progreso
  progressMessage: string = ''; // Mensaje de progreso
  progressPercentage: number = 0; // Porcentaje de progreso (simulado)
  timeoutSeconds: number = 60; // Tiempo máximo en segundos
  timeLeft: number = 0; // Tiempo restante
  
  private progressInterval: Subscription | null = null;
  private timeoutInterval: Subscription | null = null;
  private updateSubscription: Subscription | null = null;

  constructor(
    private sparePartService: SparePartsService
  ) { }

  ngOnInit(): void {
  }
  
  ngOnDestroy(): void {
    this.clearIntervals();
  }
  
  private clearIntervals(): void {
    if (this.progressInterval) {
      this.progressInterval.unsubscribe();
      this.progressInterval = null;
    }
    
    if (this.timeoutInterval) {
      this.timeoutInterval.unsubscribe();
      this.timeoutInterval = null;
    }
    
    if (this.updateSubscription) {
      this.updateSubscription.unsubscribe();
      this.updateSubscription = null;
    }
  }

  // Función para emitir el evento de cierre
  closeComponent(): void {
    this.clearIntervals();
    this.close.emit();
  }

  // Obtener el total de elementos que se actualizarán
  getTotalToUpdate(): number {
    return this.isSelectingAllFiltered ? this.allFilteredCount : this.selectedItems.length;
  }
  
  // Simular progreso durante la actualización
  private simulateProgress(): void {
    // Configuración de tiempo y progreso
    const totalItems = this.getTotalToUpdate();
    // Aumentar tiempo estimado por ítem y asegurar un rango mayor
    const estimatedTimePerItem = 0.2; // 200ms por elemento estimado
    // Mínimo 60 segundos, Máximo 300 segundos (5 minutos)
    const estimatedTotalTime = Math.max(60, Math.min(300, Math.ceil(totalItems * estimatedTimePerItem)));
    
    // Configurar el tiempo máximo basado en la cantidad de elementos
    this.timeoutSeconds = estimatedTotalTime;
    
    // Usamos un intervalo para actualizar el progreso cada 300ms
    const steps = Math.floor(estimatedTotalTime * 3.33); // ~3 actualizaciones por segundo
    const increment = 90 / steps; // Incremento por paso (llegamos al 90%)
    
    // Crear un array de mensajes de progreso con estimaciones
    const progressMessages = [
      `Iniciando actualización de ${totalItems} repuestos...`,
      `Conectando con la base de datos...`,
      `Preparando la actualización masiva...`,
      `Procesando repuestos (esta operación puede tardar hasta ${estimatedTotalTime} segundos)...`,
      `Aplicando nuevo estado "${this.selectedStatus}"...`,
      `Actualizando ${Math.min(100, Math.floor(totalItems * 0.1))} repuestos...`,
      `Actualizando ${Math.min(200, Math.floor(totalItems * 0.2))} repuestos...`,
      `Actualizando ${Math.min(500, Math.floor(totalItems * 0.5))} repuestos...`,
      `Verificando integridad de los datos...`,
      `Confirmando cambios en la base de datos...`,
      `Sincronizando información...`,
      `Finalizando proceso...`
    ];
    
    // Iniciar desde 0
    this.progressPercentage = 0;
    
    // Iniciar el contador de tiempo
    this.timeLeft = this.timeoutSeconds;
    this.timeoutInterval = interval(1000).subscribe(() => {
      this.timeLeft--;
      
      // Si llegamos a un tiempo crítico, mostrar mensaje más urgente
      if (this.timeLeft <= 5) {
        this.progressMessage = `¡Tiempo crítico! Completando operación en ${this.timeLeft} segundos...`;
      }
      
      // Si llegamos a 0, cancelamos la operación
      if (this.timeLeft <= 0) {
        this.handleTimeout();
      }
    });
    
    // Usar intervalo para actualizar el progreso
    this.progressInterval = interval(300).pipe(
      take(steps + 5) // Tomamos más pasos para llegar cerca del 100%
    ).subscribe(i => {
      // Calcular el nuevo porcentaje, sin pasar del 95%
      const newPercentage = Math.min(95, (i + 1) * increment);
      this.progressPercentage = Math.floor(newPercentage);
      
      // Actualizar el mensaje de progreso de forma cíclica
      if (i < progressMessages.length) {
        this.progressMessage = progressMessages[i];
      } else {
        // Para los últimos pasos, mostrar mensajes basados en el progreso
        const processedEstimate = Math.floor((this.progressPercentage / 100) * totalItems);
        this.progressMessage = `Procesando aproximadamente ${processedEstimate} de ${totalItems} repuestos...`;
      }
    });
  }
  
  // Método para manejar timeout
  private handleTimeout(): void {
    this.clearIntervals();
    
    console.error('La operación de actualización ha excedido el tiempo máximo');
    
    if (this.isProcessing) {
      this.isProcessing = false;
      
      // Cancelar la suscripción para evitar que continúe procesando
      if (this.updateSubscription) {
        this.updateSubscription.unsubscribe();
        this.updateSubscription = null;
      }
      
      // Notificar al usuario y al componente padre
      this.progressMessage = 'La operación ha excedido el tiempo máximo.';
      this.statusChange.emit({status: this.selectedStatus, success: false});
      
      alert('La operación ha excedido el tiempo máximo de espera. Por favor, intente con una selección más pequeña o filtros más específicos.');
    }
  }

  // Función para aplicar el cambio de estado
  async applyStatusChange(): Promise<void> {
    // Agregar alerta de confirmación
    const totalItems = this.getTotalToUpdate();
    const r = confirm(`¿Está seguro de cambiar el estado de ${totalItems} repuestos seleccionados?`);
    if (r == false) {
      return;
    }
    
    if (!this.selectedStatus) {
      alert("Por favor, seleccione un estado");
      return;
    }
    
    this.isProcessing = true;
    
    // Iniciar simulación de progreso
    this.simulateProgress();
    
    try {
      if (this.isSelectingAllFiltered) {
        // Actualizar todos los repuestos filtrados en el servidor
        this.progressMessage = `Actualizando ${this.allFilteredCount} repuestos...`;
        
        this.updateSubscription = this.sparePartService.updateAllFilteredSparePartsStatus(
          this.selectedStatus,
          this.searchTerm,
          this.startDate,
          this.endDate
        ).subscribe(
          (response) => {
            // Completar el progreso al 100%
            this.progressPercentage = 100;
            this.progressMessage = '¡Actualización completada con éxito!';
            
            // Limpiar intervalos
            this.clearIntervals();
            
            // Emitir evento de éxito después de un breve retraso para mostrar el 100%
            setTimeout(() => {
              this.statusChange.emit({status: this.selectedStatus, success: true});
              this.closeComponent();
            }, 500);
          },
          (error) => {
            console.error('Error en la actualización masiva:', error);
            
            // Limpiar intervalos
            this.clearIntervals();
            
            this.progressMessage = 'Error en la actualización';
            this.isProcessing = false;
            this.statusChange.emit({status: this.selectedStatus, success: false});
          }
        );
      } else {
        // Actualizar solo los repuestos seleccionados
        this.progressMessage = `Actualizando ${this.selectedItems.length} repuestos...`;
        
        this.updateItemsStatus(this.selectedStatus).then((data) => {
          this.updateSubscription = this.sparePartService.updateSpareParts(data).subscribe(
            (response) => {
              // Completar el progreso al 100%
              this.progressPercentage = 100;
              this.progressMessage = '¡Actualización completada con éxito!';
              
              // Limpiar intervalos
              this.clearIntervals();
              
              // Emitir evento de éxito después de un breve retraso para mostrar el 100%
              setTimeout(() => {
                this.statusChange.emit({status: this.selectedStatus, success: true});
                this.closeComponent();
              }, 500);
            },
            (error) => {
              console.error('Error al actualizar los repuestos:', error);
              
              // Limpiar intervalos
              this.clearIntervals();
              
              this.progressMessage = 'Error en la actualización';
              this.isProcessing = false;
              this.statusChange.emit({status: this.selectedStatus, success: false});
            }
          );
        });
      }
    } catch (error) {
      console.error('Error en el proceso de actualización:', error);
      
      // Limpiar intervalos
      this.clearIntervals();
      
      this.progressMessage = 'Error en la actualización';
      this.isProcessing = false;
      this.statusChange.emit({status: this.selectedStatus, success: false});
    }
  }

  async updateItemsStatus(status: string): Promise<SparePartModel[]> {
    return new Promise((resolve) => {
      // Crear una copia del array para evitar problemas de referencia
      const updatedItems = [...this.selectedItems];
      
      // Actualizar el estado de cada elemento
      updatedItems.forEach(item => {
        item.productStatus = status;
      });
      
      resolve(updatedItems);
    });
  }
}